import React, { useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Icons } from "@/components/icons";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";
import { SonarAnimation, BackgroundEffects } from "@/components/landing";

interface LandingLayoutProps {
  children?: React.ReactNode;
}

export const LandingLayout: React.FC<LandingLayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const currentYear = new Date().getFullYear();

  const navLinks = [
    { label: "Features", href: "/features" },
    { label: "How it works", href: "/how-it-works" },
    { label: "Pricing", href: "/pricing" },
  ];

  const footerLinks = {
    product: [
      { label: 'Features', href: '/features' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'How it works', href: '/how-it-works' },
      { label: 'Enterprise', href: '/enterprise' },
    ],
    resources: [
      { label: 'Blog', href: '/blog' },
      { label: 'Case Studies', href: '/case-studies' },
      { label: 'Help Center', href: '/help' },
      { label: 'API Docs', href: '/docs' },
    ],
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Careers', href: '/careers' },
      { label: 'Legal', href: '/legal' },
      { label: 'Contact', href: '/contact' },
    ],
  };

  return (
    <div className="min-h-screen landing-gradient-bg relative overflow-x-hidden">
      {/* Animated Background */}
      <SonarAnimation />
      <BackgroundEffects />

      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Navigation */}
        <header className="landing-glass border-b border-white/10">
          <div className="container mx-auto flex h-16 items-center justify-between px-4 sm:px-8">
            <Link to="/" className="flex items-center space-x-2">
              <div className="p-2 rounded-lg landing-glass">
                <Icons.logo className="h-6 w-6 text-cyan-400" />
              </div>
              <span className="font-semibold text-xl landing-gradient-text">Discovery Hub</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm font-medium text-white hover:text-white/80 transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="hidden md:block">
              <Button asChild className="landing-btn-primary border-0">
                {user ? (
                  <Link to="/app">Dashboard</Link>
                ) : (
                  <Link to="/auth">Join Beta</Link>
                )}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-[hsl(var(--landing-text))]"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden landing-glass border-t border-white/10 px-4 py-4">
              <nav className="flex flex-col space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-sm font-medium text-white hover:text-white/80 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
                <Button asChild className="landing-btn-primary border-0 w-full">
                  {user ? (
                    <Link to="/app">Dashboard</Link>
                  ) : (
                    <Link to="/auth">Join Beta</Link>
                  )}
                </Button>
              </nav>
            </div>
          )}
        </header>

        {/* Main Content */}
        <main className="flex-1">
          {children || <Outlet />}
        </main>

        {/* Footer */}
        <footer className="py-16 px-4 border-t border-white/5">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
              {/* Brand */}
              <div className="col-span-2 md:col-span-1">
                <Link to="/" className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-lg landing-glass">
                    <Icons.logo className="w-5 h-5 text-cyan-400" />
                  </div>
                  <span className="font-bold text-white">Discovery Hub</span>
                </Link>
                <p className="text-sm text-[hsl(var(--landing-muted))] max-w-xs">
                  AI-powered B2B lead generation platform for modern sales teams.
                </p>
              </div>

              {/* Product */}
              <div>
                <h4 className="font-semibold text-white mb-4">Product</h4>
                <ul className="space-y-3">
                  {footerLinks.product.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.href}
                        className="text-sm text-[hsl(var(--landing-muted))] hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Resources */}
              <div>
                <h4 className="font-semibold text-white mb-4">Resources</h4>
                <ul className="space-y-3">
                  {footerLinks.resources.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.href}
                        className="text-sm text-[hsl(var(--landing-muted))] hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Company */}
              <div>
                <h4 className="font-semibold text-white mb-4">Company</h4>
                <ul className="space-y-3">
                  {footerLinks.company.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.href}
                        className="text-sm text-[hsl(var(--landing-muted))] hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Copyright */}
            <div className="pt-8 border-t border-white/5 text-center">
              <p className="text-sm text-[hsl(var(--landing-muted))]">
                © {currentYear} Discovery Hub. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};
